// src/components/Book.jsx (or wherever you keep it)
import React, { useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import ElectricRickshawIcon from "@mui/icons-material/ElectricRickshaw";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";

// ──────────────────────────────────────────────────────────────
// SERVICE TYPES - MUST BE DEFINED BEFORE ANY useState()
// ──────────────────────────────────────────────────────────────
const serviceTypes = [
  {
    value: "cab",
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <DirectionsCarIcon sx={{ fontSize: 20, color: "#098535" }} />
        <span>Cab</span>
      </div>
    ),
  },
  {
    value: "tricycle",
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <ElectricRickshawIcon sx={{ fontSize: 20, color: "#098535" }} />
        <span>Tricycle (Keke)</span>
      </div>
    ),
  },
  {
    value: "motorcycle",
    label: (
      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <TwoWheelerIcon sx={{ fontSize: 20, color: "#098535" }} />
        <span>Motorcycle (Okada)</span>
      </div>
    ),
  },
];

// Lagos locations
const lagosLocations = [
  "Lekki Phase 1",
  "Victoria Island",
  "Ikoyi",
  "Ikeja",
  "Surulere",
  "Yaba",
  "Apapa",
  "Lagos Island",
  "Maryland",
  "Gbagada",
  "Ojota",
  "Ketu",
  "Oshodi",
  "Mushin",
  "Agege",
  "Ikorodu",
  "Ajah",
  "Badagry",
  "Epe",
  "Ilupeju",
  "Magodo",
  "Ogba",
  "Omole",
  "Anthony bâton",
  "Amuwo Odofin",
  "Festac Town",
  "Satellite Town",
  "Alimosho",
  "Egbeda",
  "Ipaja",
  "Isolo",
  "Obalende",
  "Marina",
  "CMS",
  "Idumota",
  "Broad Street",
  "Adeniji Adele",
  "Ebute Metta",
  "Bariga",
  "Somolu",
  "Kosofe",
];

const Book = () => {
  const navigate = useNavigate();
  const today = new Date();

  // States
  const [selectedDate, setSelectedDate] = useState(today);
  const [selectedTime, setSelectedTime] = useState("Now");
  const [pickupLocation, setPickupLocation] = useState("");
  const [dropoffLocation, setDropoffLocation] = useState("");
  const [pickupSuggestions, setPickupSuggestions] = useState([]);
  const [dropoffSuggestions, setDropoffSuggestions] = useState([]);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const [timeOptions, setTimeOptions] = useState([]);
  const [selectedService, setSelectedService] = useState(serviceTypes[0]); // Tracks Cab/Keke/Okada

  // Generate time options
  const generateTimeOptions = () => {
    const now = new Date();
    const options = ["Now"];
    const nextHour = new Date(now);
    nextHour.setHours(now.getHours() + 1);
    nextHour.setMinutes(0);
    nextHour.setSeconds(0);
    options.push(
      nextHour.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      }),
    );

    let currentTime = new Date(nextHour);
    for (let i = 0; i < 30; i++) {
      currentTime.setMinutes(currentTime.getMinutes() + 15);
      options.push(
        currentTime.toLocaleTimeString("en-US", {
          hour: "numeric",
          minute: "2-digit",
          hour12: true,
        }),
      );
      if (currentTime.getTime() - now.getTime() > 8 * 60 * 60 * 1000) break;
    }
    return options;
  };

  // Reverse geocode
  const reverseGeocode = async (lat, lon) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&addressdetails=1`,
      );
      const data = await res.json();
      if (data?.display_name) {
        const { road, neighbourhood, suburb, city } = data.address || {};
        const parts = [road, neighbourhood || suburb, city].filter(Boolean);
        return parts.length > 0
          ? parts.join(", ")
          : data.display_name.split(",")[0];
      }
      return `Near Lagos`;
    } catch {
      return `Current Location`;
    }
  };

  const getUserLocation = async () => {
    setIsGettingLocation(true);
    if (!navigator.geolocation) {
      alert("Geolocation not supported.");
      setIsGettingLocation(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const address = await reverseGeocode(
          pos.coords.latitude,
          pos.coords.longitude,
        );
        setPickupLocation(address);
        setPickupSuggestions([]);
        setIsGettingLocation(false);
      },
      () => {
        alert("Could not get your location. Please allow location access.");
        setIsGettingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  };

  const getLocationSuggestions = (input) => {
    if (!input.trim())
      return [{ type: "current", label: "Use My Current Location" }];
    const matches = lagosLocations
      .filter((loc) => loc.toLowerCase().includes(input.toLowerCase()))
      .map((loc) => ({ type: "location", label: loc }));
    return [{ type: "current", label: "Use My Current Location" }, ...matches];
  };

  const handlePickupChange = (e) => {
    const val = e.target.value;
    setPickupLocation(val);
    setPickupSuggestions(getLocationSuggestions(val));
  };

  const handleDropoffChange = (e) => {
    const val = e.target.value;
    setDropoffLocation(val);
    setDropoffSuggestions(
      getLocationSuggestions(val).filter((s) => s.type === "location"),
    );
  };

  const selectPickupSuggestion = (s) => {
    if (s.type === "current") getUserLocation();
    else {
      setPickupLocation(s.label);
      setPickupSuggestions([]);
    }
  };

  const selectDropoffSuggestion = (s) => {
    setDropoffLocation(s.label);
    setDropoffSuggestions([]);
  };

  useEffect(() => {
    setTimeOptions(generateTimeOptions());
  }, []);

  useEffect(() => {
    const close = () => {
      setPickupSuggestions([]);
      setDropoffSuggestions([]);
    };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  const timeSelectOptions = timeOptions.map((t) => ({
    value: t,
    label: t === "Now" ? "Now (ASAP)" : t,
  }));

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      height: "56px",
      border: "1px solid #e0e0e0",
      borderRadius: "12px",
      backgroundColor: "#fff",
      fontSize: "15px",
      boxShadow: "none",
      cursor: "pointer",
      "&:hover": { borderColor: "#098535" },
    }),
    placeholder: (base) => ({ ...base, color: "#aaa" }),
    singleValue: (base) => ({ ...base, color: "#333" }),
    dropdownIndicator: () => ({ display: "none" }),
    indicatorSeparator: () => ({ display: "none" }),
    menu: (base) => ({
      ...base,
      borderRadius: "12px",
      marginTop: "8px",
      boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
      maxHeight: "220px",
      overflow: "hidden",
    }),
    menuList: (base) => ({ ...base, maxHeight: "220px", padding: "8px 0" }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#098535"
        : state.isFocused
          ? "#f0f7ee"
          : "white",
      color: state.isSelected ? "white" : "#333",
      padding: "12px 18px",
      fontSize: "15px",
    }),
  };

  return (
    <>
      <style jsx>{`
        .booking-wrapper {
          padding: 80px 0;
          background: #f8f9fa;
        }
        .booking-container {
          max-width: 1300px;
          margin: 0 auto;
          padding: 0 15px;
        }
        .booking-card {
          background: white;
          padding: 50px 40px;
          border-radius: 20px;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
        }
        .booking-title {
          text-align: center;
          font-size: 36px;
          font-weight: 800;
          margin-bottom: 40px;
          color: #222;
        }
        .booking-form-group {
          position: relative;
          margin-bottom: 20px;
        }
        .booking-form-group label {
          display: block;
          margin-bottom: 8px;
          font-weight: 600;
          color: #444;
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .booking-input {
          width: 100%;
          padding: 16px 50px 16px 18px;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          font-size: 15px;
          background: #fff;
          transition: all 0.3s;
        }
        .booking-input:focus {
          outline: none;
          border-color: #098535;
          box-shadow: 0 0 0 4px rgba(78, 172, 61, 0.1);
        }
        .booking-icon {
          position: absolute;
          right: 18px;
          top: 52px;
          color: #888;
          font-size: 18px;
          pointer-events: none;
        }
        .location-suggestions {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background: white;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          max-height: 200px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          margin-top: 4px;
        }
        .suggestion-item {
          padding: 12px 16px;
          cursor: pointer;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .suggestion-item:hover {
          background-color: #f8f9fa;
        }
        .current-location-suggestion {
          background-color: #f0f7ee;
          font-weight: 600;
          color: #098535;
        }
        .current-location-suggestion:hover {
          background-color: #e8f4e5;
        }
        .booking-datepicker .react-datepicker-wrapper {
          display: block;
        }
        .booking-datepicker input {
          width: 100%;
          padding: 16px 50px 16px 18px;
          border: 1px solid #e0e0e0;
          border-radius: 12px;
          background: white;
          font-size: 15px;
        }
        .booking-submit-btn {
          background: #098535;
          color: white;
          border: none;
          padding: 18px 40px;
          border-radius: 50px;
          font-size: 18px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.4s;
          width: 100%;
          max-width: 400px;
          margin: 40px auto 0;
          display: block;
        }
        .booking-submit-btn:hover {
          background: #e63900;
          transform: translateY(-4px);
          box-shadow: 0 15px 30px rgba(255, 72, 0, 0.3);
        }
        @media (max-width: 992px) {
          .booking-card {
            padding: 40px 25px;
          }
          .booking-title {
            font-size: 30px;
          }
        }
      `}</style>

      <div className="booking-wrapper">
        <div className="booking-container">
          <div className="booking-card">
            <h4 className="booking-title">Book Your Ride</h4>

            <form>
              {/* ROW 1 */}
              <div className="row g-3 g-md-4">
                <div className="col-12 col-md-6 col-lg-4">
                  <div className="booking-form-group">
                    <label>Pick Up Location</label>
                    <input
                      type="text"
                      className="booking-input"
                      placeholder="Type or use current location"
                      value={pickupLocation}
                      onChange={handlePickupChange}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPickupSuggestions(
                          getLocationSuggestions(pickupLocation),
                        );
                      }}
                    />
                    <i className="far fa-location-dot booking-icon"></i>
                    {pickupSuggestions.length > 0 && (
                      <div
                        className="location-suggestions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {pickupSuggestions.map((s, i) => (
                          <div
                            key={i}
                            className={`suggestion-item ${s.type === "current" ? "current-location-suggestion" : ""}`}
                            onClick={() => selectPickupSuggestion(s)}
                          >
                            {s.type === "current" && isGettingLocation ? (
                              <>Getting location...</>
                            ) : (
                              s.label
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <div className="booking-form-group">
                    <label>Drop Off Location</label>
                    <input
                      type="text"
                      className="booking-input"
                      placeholder="Enter destination"
                      value={dropoffLocation}
                      onChange={handleDropoffChange}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDropoffSuggestions(
                          getLocationSuggestions(dropoffLocation).filter(
                            (s) => s.type === "location",
                          ),
                        );
                      }}
                    />
                    <i className="far fa-location-dot booking-icon"></i>
                    {dropoffSuggestions.length > 0 && (
                      <div
                        className="location-suggestions"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {dropoffSuggestions.map((s, i) => (
                          <div
                            key={i}
                            className="suggestion-item"
                            onClick={() => selectDropoffSuggestion(s)}
                          >
                            {s.label}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-6 col-lg-4">
                  <div className="booking-form-group">
                    <label>Service Type</label>
                    <Select
                      options={serviceTypes}
                      styles={customSelectStyles}
                      value={selectedService}
                      onChange={setSelectedService}
                      placeholder="Choose Service"
                      isSearchable={false}
                    />
                  </div>
                </div>
              </div>

              {/* ROW 2 */}
              <div className="row g-3 g-md-4 mt-2">
                <div className="col-12 col-md-6 col-lg-6">
                  <div className="booking-form-group booking-datepicker">
                    <label>Pick Up Date</label>
                    <DatePicker
                      selected={selectedDate}
                      onChange={setSelectedDate}
                      className="booking-input"
                      dateFormat="MMM dd, yyyy"
                      minDate={new Date()}
                      customInput={
                        <input
                          placeholder="Today"
                          value={
                            selectedDate.toDateString() === today.toDateString()
                              ? "Today"
                              : selectedDate.toLocaleDateString("en-US", {
                                  month: "short",
                                  day: "numeric",
                                  year: "numeric",
                                })
                          }
                          readOnly
                        />
                      }
                    />
                    <i className="far fa-calendar-days booking-icon"></i>
                  </div>
                </div>

                <div className="col-12 col-md-6 col-lg-6">
                  <div className="booking-form-group">
                    <label>Pick Up Time</label>
                    <Select
                      options={timeSelectOptions}
                      value={
                        timeSelectOptions.find(
                          (o) => o.value === selectedTime,
                        ) || timeSelectOptions[0]
                      }
                      onChange={(opt) => setSelectedTime(opt.value)}
                      styles={customSelectStyles}
                      placeholder="Now (ASAP)"
                      isSearchable={false}
                    />
                    <i className="far fa-clock booking-icon"></i>
                  </div>
                </div>
              </div>

              {/* BOOK NOW BUTTON */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    if (!pickupLocation.trim() || !dropoffLocation.trim()) {
                      alert("Please fill both pickup and dropoff locations");
                      return;
                    }
                    navigate("/ride-options", {
                      state: {
                        pickupLocation,
                        dropoffLocation,
                        serviceType: selectedService.value, // cab | tricycle | motorcycle
                      },
                    });
                  }}
                  className="booking-submit-btn"
                >
                  See Prices
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default Book;
